/**
 *  @fileOverview General helper functions
 *
 *  @author       Peter Schmiz <peter.schmiz@possible.com>
 *
 *  @requires     NPM:lodash
 *  @requires     NPM:detect-it
 *  @requires     utils/object-fit
 *  @requires     utils/develop
 *  @requires     utils/svg-sprite-loader
 */

import detectIt from 'detect-it';
import throttle from 'lodash/throttle';
import { objectFit } from './object-fit';
import { develop } from './develop';
import loadSvgSprite from './svg-sprite-loader';

const DEBUG_HOSTS = /localhost/ig;
let scrollTimerId;

/**
 * General page helper methods and utilities
 */

class Helper {
  constructor() {
    this.inited = false;
  }

  init() {
    loadSvgSprite();

    if ('objectFit' in document.documentElement.style === false) {
      window.setTimeout(() => {
        objectFit.init();
      }, 500);
      window.addEventListener('resize', throttle(() => {
        objectFit.update();
      }, 100));
    }

    this.onScrollBind = this.onScroll.bind(this);
    if (detectIt.deviceType === 'mouseOnly') {
      this.initScrollPerformance();
    }

    if (detectIt.deviceType !== 'mouseOnly') {
      document.documentElement.classList.remove('no-touch');
      document.documentElement.classList.add('touch');
    }

    if (
      window.location &&
      window.location.host &&
      window.location.host.match(DEBUG_HOSTS) !== null &&
      this.inited === false
    ) {
      develop.init();
    }
  }

  initScrollPerformance() {
    window.addEventListener(
      'scroll',
      this.onScrollBind,
      detectIt.passiveEvents ? { capture: false, passive: true } : false
    );
  }

  onScroll() {
    window.clearTimeout(scrollTimerId);
    if (document.body.querySelector('main').classList.contains('no-hover') === false) {
      this.disableHover();
    }

    scrollTimerId = setTimeout(() => {
      this.enableHover();
    }, 500);
  }

  disableHover() {
    document.body.querySelector('main').classList.add('no-hover');
  }

  enableHover() {
    document.body.querySelector('main').classList.remove('no-hover');
  }

  getParentNode(elem, selector) {
    const elements = [];
    const hasSelector = selector !== undefined;
    let element = elem;

    /* eslint-disable no-cond-assign, no-continue */
    while ((element = element.parentElement) !== null) {
      if (element.nodeType !== Node.ELEMENT_NODE) {
        continue;
      }
      if (!hasSelector || this.selectorMatches(element, selector)) {
        elements.push(element);
      }
    }
    /* eslint-enable no-cond-assign, no-continue */

    return elements;
  }

  selectorMatches(el, selector) {
    const p = Element.prototype;
    const m = s => (
      [].indexOf.call(document.querySelectorAll(s), this) !== -1
    );
    const f = p.matches ||
      p.webkitMatchesSelector ||
      p.mozMatchesSelector ||
      p.msMatchesSelector ||
      m;

    return f.call(el, selector);
  }

  /* eslint-disable no-bitwise, no-param-reassign */

  lightenDarkenColor(col, amt) {
    let usePound = false;

    if (col[0] === '#') {
      col = col.slice(1);
      usePound = true;
    }

    const num = parseInt(col, 16);
    let r = (num >> 16) + amt;
    if (r > 255) {
      r = 255;
    } else if (r < 0) {
      r = 0;
    }

    let b = ((num >> 8) & 0x00FF) + amt;

    if (b > 255) {
      b = 255;
    } else if (b < 0) {
      b = 0;
    }

    let g = (num & 0x0000FF) + amt;

    if (g > 255) {
      g = 255;
    } else if (g < 0) {
      g = 0;
    }

    return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16);
  }

  isLightColor(col) {
    if (col[0] === '#') {
      col = col.slice(1);
    }

    const num = parseInt(col, 16);
    const r = (num >> 16);
    const g = ((num >> 8) & 0x00FF);
    const b = (num & 0x0000FF);

    return (r > 200 && g > 200 && b > 200);
  }

  colorToHex(color) {
    if (color.substr(0, 1) === '#') {
      return color;
    }
    let digits = /(.*?)rgb\((\d+), (\d+), (\d+)\)/.exec(color);
    if (!digits) {
      digits = /(.*?)rgba\((\d+), (\d+), (\d+), (\d+)\)/.exec(color);
    }

    const red = parseInt(digits[2], 10);
    const green = parseInt(digits[3], 10);
    const blue = parseInt(digits[4], 10);

    const rgb = blue | (green << 8) | (red << 16);

    return `${digits[1]}#${rgb.toString(16)}`;
  }

  /* eslint-enable no-bitwise */
}

export const helper = new Helper();



// WEBPACK FOOTER //
// ./src/js/utils/helper.js